import React, { useState } from 'react';
import swal from 'sweetalert';

const AstrologerListModal = ({ show, onHide }) => {
    const [activeTab, setActiveTab] = useState("basic");
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState({
        name: "",
        mobile: "",
        email: "",
        dob: "",
        dot: "",
        placeOfBirth: "",
        address: "",
        city: "",
        pinCode: "",
        panCard: "",
        aadhar: "",
        maritalStatus: "",
        gender: "",
        image: null,
        imagePreview: null,
        aboutMe: "",
        qualification: "",
        bankName: "",
        accountNumber: "",
        ifscCode: "",
        branchName: "",
        attachment: null,
        attachmentName: "",
        attachmentPreview: null,
        review: "",
    });

    const validateBasicForm = () => {
        const newErrors = {};

        // Basic Details Validation
        if (!form.name.trim()) {
            newErrors.name = "Name is required";
        } else if (form.name.length < 3) {
            newErrors.name = "Name must be at least 3 characters";
        }

        if (!form.mobile.trim()) {
            newErrors.mobile = "Mobile number is required";
        } else if (!/^[6-9]\d{9}$/.test(form.mobile)) {
            newErrors.mobile = "Please enter a valid 10-digit mobile number";
        }

        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            newErrors.email = "Please enter a valid email address";
        }

        if (!form.dob) {
            newErrors.dob = "Date of birth is required";
        } else {
            const dobDate = new Date(form.dob);
            const today = new Date();
            if (dobDate > today) {
                newErrors.dob = "Date of birth cannot be in the future";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isBasicFormValid = () => {
        return form.name.trim().length >= 3 &&
            /^[6-9]\d{9}$/.test(form.mobile) &&
            form.dob &&
            (!form.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email));
    };

    const handleTabClick = (tab) => {
        if (tab !== "basic" && !isBasicFormValid()) {
            swal('Warning', 'Please complete the basic information first!', 'warning');
            return;
        }
        setActiveTab(tab);
    };

    const handleFormChange = (e) => {
        const { name, value, files } = e.target;

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }

        if (name === "image") {
            const file = files[0];
            if (file) {
                if (file.size > 5 * 1024 * 1024) { // 5MB limit
                    setErrors(prev => ({ ...prev, image: "Image size should be less than 5MB" }));
                    return;
                }
                if (!file.type.startsWith('image/')) {
                    setErrors(prev => ({ ...prev, image: "Please upload an image file" }));
                    return;
                }
                setForm((prev) => ({
                    ...prev,
                    image: file,
                    imagePreview: URL.createObjectURL(file),
                }));
            }
        } else if (name === "attachment") {
            const file = files[0];
            if (file) {
                if (file.size > 10 * 1024 * 1024) { // 10MB limit
                    setErrors(prev => ({ ...prev, attachment: "File size should be less than 10MB" }));
                    return;
                }
                const allowedTypes = ['.pdf', '.doc', '.docx'];
                const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
                if (!allowedTypes.includes(fileExtension)) {
                    setErrors(prev => ({ ...prev, attachment: "Please upload PDF or Word document" }));
                    return;
                }
                setForm((prev) => ({
                    ...prev,
                    attachment: file,
                    attachmentName: file.name,
                    attachmentPreview: URL.createObjectURL(file),
                }));
            }
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (validateBasicForm()) {
          
            swal('Success', 'Profile saved successfully!', 'success');
            onHide();
        } else {
            swal('Error', 'Please fix the errors in the form', 'error');
        }
    };

    const renderFormContent = () => {
        switch (activeTab) {
            case "basic":
                return (
                    <div style={styles.twoColumnGrid}>
                        <InputRow
                            label="Name *"
                            name="name"
                            value={form.name}
                            onChange={handleFormChange}
                            required={true}
                            errors={errors}
                        />
                        <InputRow
                            label="Mobile# *"
                            name="mobile"
                            value={form.mobile}
                            onChange={handleFormChange}
                            required={true}
                            errors={errors}
                        />
                        <InputRow
                            label="Email ID"
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleFormChange}
                            errors={errors}
                        />
                        <InputRow
                            label="DOB *"
                            type="date"
                            name="dob"
                            value={form.dob}
                            onChange={handleFormChange}
                            required={true}
                            errors={errors}
                        />
                        <InputRow
                            label="DOT"
                            type="date"
                            name="dot"
                            value={form.dot}
                            onChange={handleFormChange}
                            errors={errors}
                        />
                        <InputRow
                            label="Place of Birth"
                            name="placeOfBirth"
                            value={form.placeOfBirth}
                            onChange={handleFormChange}
                            errors={errors}
                        />
                        <div style={styles.fullWidth}>
                            <TextAreaRow
                                label="Address"
                                name="address"
                                value={form.address}
                                onChange={handleFormChange}
                                errors={errors}
                            />
                        </div>
                        <InputRow
                            label="City"
                            name="city"
                            value={form.city}
                            onChange={handleFormChange}
                            errors={errors}
                        />
                        <InputRow
                            label="PIN Code"
                            type="number"
                            name="pinCode"
                            value={form.pinCode}
                            onChange={handleFormChange}
                            errors={errors}
                        />
                        <InputRow
                            label="PAN Card#"
                            name="panCard"
                            value={form.panCard}
                            onChange={handleFormChange}
                            maxLength={10}
                            errors={errors}
                        />
                        <InputRow
                            label="Aadhar#"
                            name="aadhar"
                            value={form.aadhar}
                            onChange={handleFormChange}
                            maxLength={14}
                            errors={errors}
                        />
                        <SelectRow
                            label="Marital Status"
                            name="maritalStatus"
                            value={form.maritalStatus}
                            onChange={handleFormChange}
                            options={["", "Single", "Married", "Divorced", "Widowed"]}
                            errors={errors}
                        />
                        <SelectRow
                            label="Gender"
                            name="gender"
                            value={form.gender}
                            onChange={handleFormChange}
                            options={["", "Male", "Female", "Other"]}
                            errors={errors}
                        />
                        <div style={styles.fullWidth}>
                            <FileInputRow
                                label="Image"
                                name="image"
                                onChange={handleFormChange}
                                preview={form.imagePreview}
                                previewAlt="Profile Image Preview"
                                errors={errors}
                            />
                        </div>
                    </div>
                );
            case "about":
                return (
                    <div style={styles.fullWidth}>
                        <TextAreaRow
                            label="About Me"
                            name="aboutMe"
                            value={form.aboutMe}
                            onChange={handleFormChange}
                            placeholder="Write something about yourself..."
                            errors={errors}
                        />
                    </div>
                );
            case "qualification":
                return (
                    <div style={styles.fullWidth}>
                        <TextAreaRow
                            label="Qualification"
                            name="qualification"
                            value={form.qualification}
                            onChange={handleFormChange}
                            placeholder="Enter your educational qualifications..."
                            errors={errors}
                        />
                    </div>
                );
            case "bank":
                return (
                    <div style={styles.twoColumnGrid}>
                        <InputRow
                            label="Bank Name"
                            name="bankName"
                            value={form.bankName}
                            onChange={handleFormChange}
                            errors={errors}
                        />
                        <InputRow
                            label="Account Number"
                            name="accountNumber"
                            value={form.accountNumber}
                            onChange={handleFormChange}
                            errors={errors}
                        />
                        <InputRow
                            label="IFSC Code"
                            name="ifscCode"
                            value={form.ifscCode}
                            onChange={handleFormChange}
                            errors={errors}
                        />
                        <InputRow
                            label="Branch Name"
                            name="branchName"
                            value={form.branchName}
                            onChange={handleFormChange}
                            errors={errors}
                        />
                    </div>
                );
            case "attachment":
                return (
                    <div style={styles.fullWidth}>
                        <FileInputRow
                            label="Attachment"
                            name="attachment"
                            onChange={handleFormChange}
                            preview={form.attachmentPreview}
                            previewAlt={form.attachmentName || "Attachment Preview"}
                            errors={errors}
                        />
                        {form.attachmentName && <p>File: {form.attachmentName}</p>}
                    </div>
                );
            case "review":
                return (
                    <div style={styles.fullWidth}>
                        <TextAreaRow
                            label="Review"
                            name="review"
                            value={form.review}
                            onChange={handleFormChange}
                            placeholder="Add any review or notes here..."
                            errors={errors}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    if (!show) return null;

    return (
        <div className="custom-modal-overlay" style={styles.overlay}>
            <div className="custom-modal" style={styles.modal}>
                <div className="custom-modal-header" style={styles.header}>
                    <h3 style={styles.title}>Astrologer Profile</h3>
                    <button onClick={onHide} style={styles.closeButton}>×</button>
                </div>
                <div className="custom-modal-body" style={styles.body}>
                    <div style={styles.tabContainer}>
                        {["basic", "about", "qualification", "bank", "attachment", "review"].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => handleTabClick(tab)}
                                style={{
                                    ...styles.tabButton,
                                    borderBottom: activeTab === tab ? "3px solid #FF6600" : "3px solid transparent",
                                    fontWeight: activeTab === tab ? "700" : "400",
                                    opacity: tab !== "basic" && !isBasicFormValid() ? "0.5" : "1",
                                    cursor: tab !== "basic" && !isBasicFormValid() ? "not-allowed" : "pointer",
                                }}
                                disabled={tab !== "basic" && !isBasicFormValid()}
                            >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                        ))}
                    </div>
                    <form onSubmit={handleFormSubmit} style={styles.form}>
                        {renderFormContent()}
                        <div style={styles.buttonContainer}>
                            <button type="button" onClick={onHide} style={styles.cancelButton}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                style={{
                                    ...styles.submitButton,
                                    opacity: !isBasicFormValid() ? "0.5" : "1",
                                    cursor: !isBasicFormValid() ? "not-allowed" : "pointer",
                                }}
                                disabled={!isBasicFormValid()}
                            >
                                Save Profile
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Reusable components
const InputRow = ({ label, name, value, onChange, type = "text", required, maxLength, placeholder, errors = {} }) => (
    <div style={styles.row}>
        <label style={styles.label}>
            {label}
            {required && <span style={styles.required}>*</span>}
        </label>
        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            maxLength={maxLength}
            placeholder={placeholder}
            style={{
                ...styles.input,
                borderColor: errors[name] ? '#dc3545' : '#FF6600'
            }}
        />
        {errors[name] && <div style={styles.errorText}>{errors[name]}</div>}
    </div>
);

const TextAreaRow = ({ label, name, value, onChange, placeholder, required = false, errors = {} }) => (
    <div style={styles.row}>
        <label style={styles.label}>
            {label}
            {required && <span style={styles.required}>*</span>}
        </label>
        <textarea
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            style={{
                ...styles.input,
                height: "100px",
                resize: "vertical",
                borderColor: errors[name] ? '#dc3545' : '#FF6600'
            }}
        />
        {errors[name] && <div style={styles.errorText}>{errors[name]}</div>}
    </div>
);

const SelectRow = ({ label, name, value, onChange, options, required = false, errors = {} }) => (
    <div style={styles.row}>
        <label style={styles.label}>
            {label}
            {required && <span style={styles.required}>*</span>}
        </label>
        <select
            name={name}
            value={value}
            onChange={onChange}
            style={{
                ...styles.input,
                borderColor: errors[name] ? '#dc3545' : '#FF6600'
            }}
        >
            {options.map((opt, i) => (
                <option key={i} value={opt}>
                    {opt || "Select"}
                </option>
            ))}
        </select>
        {errors[name] && <div style={styles.errorText}>{errors[name]}</div>}
    </div>
);

const FileInputRow = ({ label, name, onChange, preview, previewAlt, errors = {} }) => (
    <div style={styles.row}>
        <label style={styles.label}>{label}</label>
        <input
            type="file"
            accept="image/*,.pdf,.doc,.docx"
            name={name}
            onChange={onChange}
            style={{
                ...styles.input,
                borderColor: errors[name] ? '#dc3545' : '#FF6600'
            }}
        />
        {errors[name] && <div style={styles.errorText}>{errors[name]}</div>}
        {preview && (
            <img
                src={preview}
                alt={previewAlt}
                style={{ marginTop: 10, maxWidth: 150, borderRadius: 6, objectFit: "contain" }}
            />
        )}
    </div>
);

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        zIndex: 9999,
        paddingTop: '30px',
    },
    modal: {
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        marginTop: '0',
        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    header: {
        padding: '15px 20px',
        borderBottom: '1px solid #dee2e6',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: 9999,
    },
    title: {
        margin: 0,
        color: '#FF6600',
        fontSize: '1.5rem',
        textAlign: 'center',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        color: '#6c757d',
        padding: '0 5px',
    },
    body: {
        padding: '20px',
    },
    tabContainer: {
        display: 'flex',
        marginBottom: '20px',
        borderBottom: '2px solid #ddd',
    },
    tabButton: {
        flex: 1,
        padding: '10px 15px',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        border: 'none',
        fontSize: 16,
        transition: 'border-bottom-color 0.3s',
        outline: 'none',
        color: '#333',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
    },
    row: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '15px',
        width: '100%',
    },
    label: {
        fontWeight: '600',
        marginBottom: '6px',
        color: '#333',
    },
    input: {
        padding: '8px 12px',
        borderRadius: '4px',
        border: '1.5px solid #FF6600',
        fontSize: '16px',
        outline: 'none',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        marginTop: '20px',
    },
    submitButton: {
        backgroundColor: '#FF6600',
        color: '#fff',
        border: 'none',
        padding: '8px 15px',
        borderRadius: '6px',
        fontWeight: '700',
        fontSize: '16px',
        cursor: 'pointer',
    },
    cancelButton: {
        backgroundColor: '#6c757d',
        color: '#fff',
        border: 'none',
        padding: '8px 15px',
        borderRadius: '6px',
        fontWeight: '700',
        fontSize: '16px',
        cursor: 'pointer',
    },
    twoColumnGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '15px',
        width: '100%',
    },
    fullWidth: {
        gridColumn: '1 / -1',
        width: '100%',
    },
    required: {
        color: '#dc3545',
        marginLeft: '4px',
    },
    errorText: {
        color: '#dc3545',
        fontSize: '12px',
        marginTop: '4px',
    },
};

export default AstrologerListModal; 